import { useState, type FormEvent, type ReactElement } from "react";

import { api, ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { TabPanel } from "../Tabs";

// Collaborator account: just an email + password login — no demographics, no
// consent, none of the participant-profile machinery. Changing the password
// revokes every session, so we sign out afterwards.
export function CollabAccountTab(): ReactElement {
  const { user, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function changePassword(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const resp = await api<{ detail: string }>("POST", "/auth/password/change", {
        json: { current_password: currentPassword, new_password: newPassword },
      });
      setNotice(resp.detail);
      setCurrentPassword("");
      setNewPassword("");
      await logout(); // sessions are revoked server-side
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not change the password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <TabPanel id="account" title="Account">
      <dl className="account-readonly">
        <div>
          <dt>Email</dt>
          <dd>{user?.email ?? "—"}</dd>
        </div>
      </dl>
      <h3>Change Password</h3>
      <form className="q-form" onSubmit={changePassword}>
        <div className="field-grid">
          <label className="field">
            <span>Current password</span>
            <input
              type="password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </label>
          <label className="field">
            <span>New password</span>
            <input
              type="password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </label>
        </div>
        <p>
          <button className="btn-ghost btn-sm" type="submit" disabled={busy}>
            Change password
          </button>{" "}
          <span className="muted">You'll be signed out and need to log in again.</span>
        </p>
      </form>
      {notice ? <p className="muted">{notice}</p> : null}
      {error ? <p className="form-error">{error}</p> : null}
    </TabPanel>
  );
}
